/**
 * Name versions (round 19; docs/features/order-to-invoice/40_quotes_module_spec.md
 * §5, doc 43 §6.3): a line's versions got default labels (A, B, C…) at the
 * basket; here, on the quote or the order, each gets its label (up to 8
 * characters) and an optional name ("A · Bristol"). The count and split stay
 * as they are (changing them is a new quote, or cancel and add on an order).
 *
 * Shared by the quote page and the order page, each with its own title and
 * sentence. It posts through the page (`onSave`), which answers with the
 * refusal to show: one naming a version's input (`versions.1.label`) beside
 * it, anything else in the banner. The modal's own confirm is its one
 * primary button.
 */
import { useId, useState } from "react";
import { Alert, AlertDescription } from "../../primitives/alert";
import { Button } from "../../primitives/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../primitives/dialog";
import { Input } from "../../primitives/input";

/** The longest label orders' version rules accept. */
const MAX_LABEL = 8;

export interface NameableVersion {
  /** The version's position on the line, from 1. */
  position: number;
  label: string | null;
  name: string | null;
  quantity: number;
}

/** A refused Name versions: the banner, and each refused input by its request path. */
export interface NameVersionsFailure {
  summary: string;
  fieldErrors: Partial<Record<string, string>>;
}

export function NameVersionsDialog({
  title,
  description,
  versions,
  busy,
  onClose,
  onSave,
}: {
  /** "Name versions · .02 3 × Pull-up banners". */
  title: string;
  /** What naming changes here, and what it doesn't. */
  description: string;
  versions: NameableVersion[];
  busy: boolean;
  onClose: () => void;
  onSave: (
    versions: Array<{ position: number; label: string; name: string | null }>,
  ) => Promise<NameVersionsFailure | null>;
}) {
  const id = useId();
  const [rows, setRows] = useState(
    versions.map((v) => ({
      position: v.position,
      label: v.label ?? "",
      name: v.name ?? "",
    })),
  );
  const [failure, setFailure] = useState<NameVersionsFailure | null>(null);
  // As typed: the same rules the server applies; its answer still wins.
  const typedProblem = (i: number): string | null => {
    const label = rows[i]!.label.trim();
    if (!label) return "Give it a label.";
    if (label.length > MAX_LABEL) return `Up to ${MAX_LABEL} characters.`;
    if (rows.some((r, j) => j !== i && r.label.trim() === label))
      return "Each version needs its own label.";
    return null;
  };
  const hasTypedProblem = rows.some((_, i) => typedProblem(i) !== null);
  const set = (i: number, patch: Partial<(typeof rows)[number]>) => {
    setRows((current) =>
      current.map((r, j) => (j === i ? { ...r, ...patch } : r)),
    );
    setFailure(null);
  };
  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>{description}</DialogDescription>
        </DialogHeader>
        <form
          className="space-y-3"
          onSubmit={async (e) => {
            e.preventDefault();
            setFailure(null);
            const refused = await onSave(
              rows.map((r) => ({
                position: r.position,
                label: r.label.trim(),
                name: r.name.trim() || null,
              })),
            );
            setFailure(refused);
            if (!refused) onClose();
          }}
        >
          {failure && (
            <Alert variant="destructive" role="alert">
              <AlertDescription>{failure.summary}</AlertDescription>
            </Alert>
          )}
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs text-muted-foreground">
                <th className="pb-1 pr-2 font-medium">Label</th>
                <th className="pb-1 pr-2 font-medium">Name (optional)</th>
                <th className="pb-1 text-right font-medium">Qty</th>
              </tr>
            </thead>
            <tbody>
              {versions.map((v, i) => {
                const labelProblem =
                  failure?.fieldErrors[`versions.${i}.label`] ??
                  typedProblem(i);
                const nameProblem = failure?.fieldErrors[`versions.${i}.name`];
                const labelError = `${id}-label-${i}-error`;
                const nameError = `${id}-name-${i}-error`;
                return (
                  <tr key={v.position} className="align-top">
                    <td className="py-1 pr-2">
                      <Input
                        aria-label={`Label of version ${v.position}`}
                        aria-invalid={labelProblem ? true : undefined}
                        aria-describedby={labelProblem ? labelError : undefined}
                        value={rows[i]!.label}
                        maxLength={MAX_LABEL + 4}
                        onChange={(e) => set(i, { label: e.target.value })}
                        className="w-24"
                      />
                      {labelProblem && (
                        <p
                          id={labelError}
                          className="mt-1 text-xs text-red-600"
                        >
                          {labelProblem}
                        </p>
                      )}
                    </td>
                    <td className="py-1 pr-2">
                      <Input
                        aria-label={`Name of version ${v.position}`}
                        aria-invalid={nameProblem ? true : undefined}
                        aria-describedby={nameProblem ? nameError : undefined}
                        value={rows[i]!.name}
                        placeholder={i === 0 ? "Bristol" : "Bath"}
                        onChange={(e) => set(i, { name: e.target.value })}
                      />
                      {nameProblem && (
                        <p id={nameError} className="mt-1 text-xs text-red-600">
                          {nameProblem}
                        </p>
                      )}
                    </td>
                    <td className="py-1 pt-2.5 text-right tabular-nums">
                      {v.quantity.toLocaleString("en-GB")}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>
              Back
            </Button>
            <Button type="submit" disabled={busy || hasTypedProblem}>
              Save names
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
